/**
 * Thin API client over the FastAPI backend.
 *
 * Every call is defensive: the site must stay usable when the backend is down
 * (radio metadata degrades, Telegram relay reports `ok:false`, Moony answers in
 * character with a canned line).
 */

import axios from 'axios';
import { API_BASE } from './config';

const http = axios.create({
  baseURL: API_BASE,
  timeout: 20000,
});

/** POST /api/terminal/session -> { id } */
export async function createSession(username) {
  const { data } = await http.post('/terminal/session', { username });
  return data.id;
}

/** POST /api/terminal/log — fire and forget. */
export async function logCommand({ sessionId, username, command }) {
  await http.post('/terminal/log', {
    session_id: sessionId,
    username,
    command,
  });
}

/** GET /api/terminal/whoami-responses -> string[] */
export async function fetchWhoamiResponses(fallback) {
  try {
    const { data } = await http.get('/terminal/whoami-responses');
    const responses = Array.isArray(data?.responses) ? data.responses.filter(Boolean) : [];
    return responses.length ? responses : fallback;
  } catch {
    return fallback;
  }
}

/**
 * GET /api/radio/metadata
 * AzuraCast Now Playing first, Icecast status-json fallback (backend-side).
 * Shape is the v2 contract plus optional `art`, `source`, `history`.
 */
export async function fetchMetadata() {
  const { data } = await http.get('/radio/metadata');
  return {
    artist: data?.artist || 'DeadMoon Radio',
    title: data?.title || 'Unknown',
    listeners: Number.isFinite(data?.listeners) ? data.listeners : 0,
    art: data?.art || null,
    source: data?.source || null,
    history: Array.isArray(data?.history) ? data.history : [],
  };
}

/** POST /api/say — relay a website message into the Telegram group. */
export async function sendSay({ text, senderName, sessionId }) {
  const { data } = await http.post('/say', {
    text,
    sender_name: senderName,
    session_id: sessionId,
  });
  return {
    ok: Boolean(data?.ok),
    messageId: data?.message_id ?? null,
    sessionId: data?.session_id || sessionId,
    error: data?.error || null,
    relayEnabled: data?.relay_enabled !== false,
  };
}

/**
 * GET /api/inbox/{sessionId} — drain Telegram replies routed to this visitor.
 * Normalised to camelCase; `referenceText` is the visitor's own message that
 * was answered, so the UI never has to show a raw Telegram message id.
 */
export async function fetchInbox(sessionId) {
  if (!sessionId) return [];
  const { data } = await http.get(`/inbox/${encodeURIComponent(sessionId)}`);
  if (!Array.isArray(data?.replies)) return [];
  return data.replies.map((reply) => ({
    id: reply.id || null,
    messageId: reply.telegram_message_id ?? null,
    inReplyTo: reply.in_reply_to ?? null,
    text: reply.text || '',
    from: reply.sender_name || '',
    fromBot: Boolean(reply.from_bot),
    referenceText: reply.reference_text || '',
    receivedAt: reply.received_at || null,
  }));
}

/** GET /api/telegram/history — recent messages from the group's General topic. */
export async function fetchTelegramHistory(limit = 50) {
  const { data } = await http.get('/telegram/history', { params: { limit } });
  return {
    threadId: data?.thread_id ?? null,
    messages: Array.isArray(data?.messages)
      ? data.messages.map((message) => ({
          messageId: message.message_id ?? null,
          text: message.text || '',
          senderName: message.sender_name || 'someone',
          timestamp: message.timestamp || null,
          isBot: Boolean(message.is_bot),
          replyToMessageId: message.reply_to_message_id ?? null,
        }))
      : [],
  };
}

/** POST /api/moony/chat */
export async function askMoony({ message, history }) {
  const { data } = await http.post('/moony/chat', {
    message,
    conversation_history: history,
  });
  return {
    response: data?.response || '...',
    error: data?.error || null,
  };
}

/** GET /api/telegram/status — is the relay configured and polling? */
export async function fetchTelegramStatus() {
  const { data } = await http.get('/telegram/status');
  return {
    enabled: Boolean(data?.enabled),
    pollerRunning: Boolean(data?.poller_running),
    threadId: data?.thread_id ?? null,
  };
}
